import * as yup from 'yup';
import { BARCODE_TYPES } from 'library/constants/models/item';

export const getUpdatedValues = (dirtyFields, values, defaultValues = {}, isCreate = false) => {
  if (dirtyFields === true || Array.isArray(dirtyFields)) {
    return values;
  }

  const extraFields = {};

  const result = Object.fromEntries(
    Object.keys(dirtyFields).map((key) => {
      if (['tags'].includes(key)) {
        const newTagsValue = getUpdatedValues(dirtyFields[key], values[key]).reduce((ids, { value: id }) => {
          if(Number.isInteger(id)) {
            return [...ids, id];
          }

          if(!extraFields.new_tags) {
            extraFields.new_tags = [];
          }

          extraFields.new_tags.push(id);

          return ids;
        }, []);

        if(defaultValues?.[key]?.length) {
          return ['tags_ids', newTagsValue.length ? newTagsValue : []];
        }

        return ['tags_ids', newTagsValue.length ? newTagsValue : undefined];
      }

      if (['sets'].includes(key)) {
        return ['sets_ids', getUpdatedValues(dirtyFields[key], values[key]).map((id) => Number(id))];
      }

      if (['images'].includes(key)) {
        const updatedValues = getUpdatedValues(dirtyFields[key], values[key]);
        const primaryImage = updatedValues.find(({ isPrimary }) => isPrimary === true);

        if(primaryImage) {
          extraFields.primaryImage = primaryImage;
        }

        return ['image_ids', getUpdatedValues(dirtyFields[key], values[key]).map(({ id }) => id)];
      }

      if (key === 'price') {
        const val = getUpdatedValues(dirtyFields[key], values[key]);

        return [key, !Number.isNaN(parseFloat(val)) ? parseFloat(val) : null];
      }

      if (key === 'note') {
        const val = getUpdatedValues(dirtyFields[key], values[key]);

        return [key, typeof val === 'string' ? val.trim() : null];
      }

      if (key === 'count') {
        const val = getUpdatedValues(dirtyFields[key], values[key]);
        return [key, Number.isInteger(parseInt(val, 10)) ? parseInt(val, 10) : undefined];
      }

      if (key === 'category') {
        const val = getUpdatedValues(dirtyFields[key], values[key]);
        return ['category_id', Number.isInteger(val) ? val : undefined];
      }

      if (key === 'barcode') {
        if(values[key].length >= 13) {
          extraFields.barcode_type = BARCODE_TYPES.EAN;
        } else {
          extraFields.barcode_type = BARCODE_TYPES.UPC;
        }

        return ['barcode', values[key]];
      }

      if (key === 'manufacturer') {
        const val = getUpdatedValues(dirtyFields[key], values[key]);

        if(val?.[0]?.value) {
          if(!val[0]?.isNew) {
            return ['manufacturer_id', Number(val[0]?.value)];
          }

          extraFields.new_manufacturer = val[0]?.value;
          return ['manufacturer_id', null];
        }

        if(!val?.[0] && (defaultValues[key]?.[0] || Object.keys(defaultValues).length === 0) && !isCreate) {
          return ['manufacturer_id', 'empty-value'];
        }

        return ['manufacturer_id', undefined];
      }

      if (key === 'storage') {
        const val = getUpdatedValues(dirtyFields[key], values[key]);

        if(val?.[0]?.value) {
          if(!val[0]?.isNew) {
            return ['storage_id', Number(val[0]?.value)];
          }

          extraFields.new_storage = val[0]?.value;
          return ['storage_id', null];
        }

        if(!val?.[0] && (defaultValues[key]?.[0] || Object.keys(defaultValues).length === 0) && !isCreate) {
          return ['storage_id', 'empty-value'];
        }

        return ['storage_id', undefined];
      }

      return [key, getUpdatedValues(dirtyFields[key], values[key], defaultValues)];
    }),
  );

  return {
    ...result,
    ...extraFields,
  };
};

export const format = ({
  dirtyFields,
  defaultValues,
  values: { storage_id, ...values },
  isCreate,
}) => {
  const result = { ...getUpdatedValues(dirtyFields, values, defaultValues, isCreate) };
  return result;
};

export const ITEM_VALIDATION = yup.object().shape({
  name: yup.string()
    .required('Name is required'),
  // tags: yup.array().of(yup.mixed()).default([]),
  tags: yup.array().of(yup.mixed()).nullable().default([]),
  sets: yup.array().of(yup.number().required()).default([]),
  category: yup.number().optional().nullable(),
  manufacturer: yup.array().of(yup.mixed()).nullable().default([]),
  images: yup.array().of(yup.object().shape({
    id: yup.number().required(),
    url: yup.string().required(),
    isNew: yup.boolean().notRequired(),
    created_at: yup.string().notRequired(),
  })).default([]),
  colors: yup.array().of(yup.string().required()).default([]).test(
    'mustDifferFromDefaultValue',
    'Colors value wasn\'t changed',
    (value, { originalValue }) => {
      return originalValue.sort().toString() === value.sort().toString();
    },
  ),
  count: yup.mixed().test(
    'maxDigitsAfterDecimal',
    'number field must have 2 digits after decimal or less',
    (value) => {
      if (value === '') {
        return true;
      }

      return /^\d+?$/.test(value);
    },
  ).notRequired(),
  storage: yup.array().of(yup.mixed()).nullable().default([]),
  barcode: yup.string()
    // .min(12, 'Barcode is too short')
    // .max(13, 'Barcode is too long')
    .optional()
    .default(null),
  price: yup.mixed().test(
    'maxDigitsAfterDecimal',
    'Must be a valid price in the format xx.xx',
    (value) => {
      if (value === '') {
        return true;
      }

      return /^\d+(\.\d{1,2})?$/.test(value);
    },
  ).notRequired(),
  note: yup.string().optional().default(null),
  is_wishlisted: yup.boolean().default(false).nullable(),
  new_tags: yup.array().of(yup.number()).default([]),
});

export const getBatchAddEntities = (data = {}) => Object.keys(data).reduce((result, key) => {
  if(Array.isArray(data[key]) && data[key].length === 0) {
    return result;
  }

  return {
    ...result,
    [key]: data[key],
  };
}, {});
